import { throttle } from "lodash-es";
import { storeToRefs } from "pinia";
import { Ref, computed, ref, watch } from "vue";
import { useRouter } from "vue-router";

import {
    FILE_SYNC_THROTTLE_MS,
    discardDraftsAfterSync,
    useDraftManager,
} from "@/composables/draft-manager";
import { useEditManager } from "@/composables/edit-manager";
import { useEntityNameManager } from "@/composables/entity-name-manager";
import { useOrganizationManager } from "@/composables/organization-manager";
import { registerFlush } from "@/composables/pending-flush";
import {
    refreshOnCrossTabEntitySync,
    refreshOnExternalEntityChange,
    wouldClobberLocalChanges,
} from "@/composables/refresh-on-entity-change";
import { clearStaleContentOnSwitch } from "@/composables/stale-while-revalidate";
import { useStekManager } from "@/composables/stek-manager";
import {
    isAbortError,
    useLatestRequest,
} from "@/composables/use-latest-request";
import { Artefact } from "@/lib/artefacts";
import { getDescriptionArtefact } from "@/lib/artefacts/markdown";
import { api, type types } from "@/lib/openapi";
import { RelationshipDirection } from "@/lib/openapi/gen";
import { followLink } from "@/lib/openapi/helpers";
import { useCanvasSaveStatusStore } from "@/stores/canvas-save-status";
import { EntityType, useCurrentEntityManager } from "@/stores/entity-store";
import { defineEntityStore } from "@/stores/helpers";
import { useReleaseStore } from "@/stores/release-store";
import { useSyncStore } from "@/stores/sync-store";

export type BusinessEntity = types.BusinessEntity;
export type BusinessEntityRelationship = types.BusinessEntityRelationship;

// Uses the current route to determine what the 'businessEntity' should be and fetches
// the businessEntity description for you.
export const useBusinessEntityStore = defineEntityStore(
    "businessEntity",
    () => {
        const { organization } = useOrganizationManager();
        const { stek } = useStekManager();

        const isRelationshipsModified = ref(false);
        const isMetadataModified = ref(false);

        // While a cell editor on the attributes table is open, the typed
        // text lives only inside the PrimeVue editor — nothing is dirty or
        // queued until cell-edit-complete commits it into the definition.
        // The page raises this hold so the refetch guards treat an open
        // editor as dirty; without it, a completing sync (e.g. of earlier
        // attributes) passes the guards, refetches, and the response rebinds
        // the row to the server value — reverting the just-typed text.
        const attributeEditHolds = ref(0);
        const beginAttributeEdit = () => {
            attributeEditHolds.value++;
        };
        const endAttributeEdit = () => {
            attributeEditHolds.value = Math.max(
                0,
                attributeEditHolds.value - 1
            );
        };

        const businessEntity = ref<BusinessEntity | undefined>(undefined);
        const router = useRouter();
        const syncStore = useSyncStore();
        const { markPending: markCanvasSavePending } =
            useCanvasSaveStatusStore();
        const entityId = computed(() => businessEntity.value?.identity);
        const description: Ref<Artefact<"text/markdown"> | undefined> = ref();
        const definition: Ref<Artefact<"application/json"> | undefined> = ref();
        const descriptionDraft = useDraftManager({
            entityId,
            entityType: EntityType.BUSINESS_ENTITY,
            attribute: "description",
            artefact: description,
        });
        discardDraftsAfterSync(
            entityId,
            [descriptionDraft],
            EntityType.BUSINESS_ENTITY
        );
        const definitionDraft = useDraftManager({
            entityId,
            entityType: EntityType.BUSINESS_ENTITY,
            attribute: "definition",
            artefact: definition,
        });
        discardDraftsAfterSync(
            entityId,
            [definitionDraft],
            EntityType.BUSINESS_ENTITY
        );

        const { businessEntity: businessEntityInfo } =
            useCurrentEntityManager();
        const releaseStore = useReleaseStore();
        const revisionQuery = computed(() =>
            releaseStore.activeRevision
                ? new URLSearchParams({
                      revision: releaseStore.activeRevision,
                  })
                : undefined
        );

        const isFetchingBusinessEntity = ref(false);
        const isFetchingDescription = ref(false);
        const isFetchingDefinition = ref(false);
        // Latest-request guard: each updatebusinessEntity gets its own
        // AbortController + token, so a stale run neither aborts the current one
        // nor commits results over fresher data.
        const { begin, isCurrent } = useLatestRequest();
        const {
            isModified: isNameModified,
            reset: resetName,
            persist: persistName,
        } = useEntityNameManager(businessEntity);

        async function updatebusinessEntity(force = false) {
            const businessEntityId = businessEntityInfo.value?.identity;

            // In case the same businessEntity is selected, return early.
            if (
                !force &&
                businessEntity.value &&
                businessEntity.value.identity === businessEntityId
            ) {
                return;
            }

            // Start a new request: aborts the previous one and gives us our own
            // signal + token. Reading these locals (never a shared, reassignable
            // ref) is what makes the fan-in of watchers safe — see useLatestRequest.
            const { token, signal } = begin();

            if (!businessEntityId) {
                description.value = undefined;
                definition.value = undefined;
                businessEntity.value = undefined;
                return;
            }

            isFetchingBusinessEntity.value = true;
            // Stale-while-revalidate: keep the previously-loaded entity +
            // description + definition on screen while the *same* entity is
            // refetched (a revision switch or a background sync), so it doesn't
            // blank the page or flash the spinner. The sub-fetches below always
            // overwrite for the current entity. Switching to a *different* entity
            // is handled by clearStaleContentOnSwitch, which blanks these refs.

            try {
                const { data, response } =
                    await api.startDomainBusinessEntityMgtGetBusinessEntityStartPost(
                        {
                            body: {
                                business_entity_id: `${businessEntityId}`,
                            },
                            signal,
                        }
                    );

                // A newer request superseded us while fetching — commit nothing.
                if (!isCurrent(token)) {
                    return;
                }

                if (!response.ok) {
                    if (response.status === 404) {
                        router.replace({
                            name: "not-found",
                            params: {
                                orgName: organization.value?.name,
                                stekName: stek.value?.name,
                            },
                            query: {
                                type: "businessEntity",
                                id: businessEntityId,
                            },
                        });
                    }
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                // Landing-time clobber guard: edits may have appeared while
                // this GET was in flight — the notification-time guard can't
                // see them. Only a same-entity refetch is dropped; navigating
                // to a different entity must always commit. A dropped commit
                // self-heals: the edit's own sync re-notifies and refetches.
                if (
                    businessEntityId === businessEntity.value?.identity &&
                    wouldClobberLocalChanges(externalRefresh)
                ) {
                    return;
                }
                businessEntity.value = data;
                // The fetched row is the new name baseline. Without this, a
                // server-side rename surfacing on a same-entity refetch would
                // read as a local edit (the name manager only re-baselines on
                // identity change) and get echoed back as a save.
                persistName();

                // Description + definition load on the SAME signal/token, so an
                // abort or a newer request cleanly supersedes them too — and
                // isFetching stays true until the definition is ready.
                if (businessEntity.value) {
                    await Promise.all([
                        updateDescription(signal, token),
                        updateDefinition(signal, token),
                    ]);
                }
            } catch (e) {
                if (isAbortError(e)) {
                    return;
                }
                throw e;
            } finally {
                // Only the current request owns the fetch flag; a stale run must
                // not flip it off while a newer one is still loading.
                if (isCurrent(token)) {
                    isFetchingBusinessEntity.value = false;
                }
            }
        }
        // Drop the previous entity's content (incl. its definition) when the
        // route switches to a different business entity, so stale-while-revalidate
        // never renders the old one on the new entity's page.
        clearStaleContentOnSwitch(
            () => businessEntityInfo.value?.identity,
            [businessEntity, description, definition]
        );
        watch(
            () => businessEntityInfo.value?.identity,
            () => {
                // A route switch abandons any open cell editor without its
                // complete/cancel events firing.
                attributeEditHolds.value = 0;
                updatebusinessEntity();
            },
            { immediate: true }
        );
        watch(
            () => releaseStore.activeRevision,
            () => {
                if (businessEntity.value) updatebusinessEntity(true);
            }
        );

        const updateDescription = async (
            signal: AbortSignal,
            token: number
        ) => {
            if (businessEntity.value) {
                try {
                    isFetchingDescription.value = true;
                    const descriptionResult = await getDescriptionArtefact(
                        businessEntity.value,
                        "businessEntityDescription",
                        signal,
                        revisionQuery.value
                    );

                    // A newer request superseded us — commit nothing. Same
                    // for edits that appeared while fetching (landing-time
                    // guard).
                    if (
                        !isCurrent(token) ||
                        wouldClobberLocalChanges(externalRefresh)
                    ) {
                        return;
                    }

                    description.value = descriptionResult;
                } catch (e) {
                    if (isAbortError(e)) {
                        return;
                    }
                    throw e;
                } finally {
                    if (isCurrent(token)) {
                        isFetchingDescription.value = false;
                    }
                }
            }
        };

        const updateDefinition = async (signal: AbortSignal, token: number) => {
            if (businessEntity.value) {
                if (businessEntity.value.definition) {
                    try {
                        isFetchingDefinition.value = true;
                        const { response, data } = await followLink<File>(
                            businessEntity.value.definition,
                            "get",
                            undefined,
                            signal,
                            revisionQuery.value
                        );

                        // A newer request superseded us — commit nothing.
                        // Same for edits that appeared while fetching
                        // (landing-time guard).
                        if (
                            !isCurrent(token) ||
                            !businessEntity.value ||
                            wouldClobberLocalChanges(externalRefresh)
                        ) {
                            return;
                        }

                        if (!response.ok) {
                            if (response.status === 404) {
                                return;
                            }
                            throw new Error(
                                `HTTP error! status: ${response.status}`
                            );
                        }

                        const content = JSON.stringify(data);
                        definition.value = new Artefact({
                            identity: businessEntity.value.definition.identity,
                            name: "businessEntityDefinition",
                            mimetype: "application/json",
                            content: content,
                            blob: new Blob([content]),
                        });
                        definition.value.links =
                            businessEntity.value.definition.links;
                    } catch (e) {
                        if (isAbortError(e)) {
                            return;
                        }
                        throw e;
                    } finally {
                        if (isCurrent(token)) {
                            isFetchingDefinition.value = false;
                        }
                    }
                } else {
                    // Create an empty definition artefact
                    definition.value = Artefact.createEmptyArtefact({
                        mimetype: "application/json",
                        identity:
                            businessEntity.value!.identity + "_definition",
                    });
                }
            }
        };

        // No isFetching short-circuit here: edits made WHILE a refetch is in
        // flight must read as modified, or the landing-time clobber guard
        // can't see them and the refetch's response overwrites the user's
        // text.
        const isModified = computed(() => {
            return Boolean(
                isNameModified.value ||
                description.value?.isModified ||
                definition.value?.isModified ||
                isRelationshipsModified.value ||
                isMetadataModified.value
            );
        });

        // One shared opts object: the notification guards and the
        // landing-time commit gates inside the update functions must agree
        // on what "would clobber local changes".
        const externalRefresh = {
            entityType: EntityType.BUSINESS_ENTITY,
            currentEntityId: () => businessEntity.value?.identity,
            isDirty: () => isModified.value || attributeEditHolds.value > 0,
            refetch: () => updatebusinessEntity(true),
        };

        refreshOnExternalEntityChange(externalRefresh);

        const queueSave = async () => {
            if (!isModified.value || !businessEntity.value) {
                return;
            }

            const updateContent: types.StartDomainBusinessEntityMgtUpdateBusinessEntityStartPostData =
                {
                    body: {
                        business_entity_id: businessEntity.value.identity,
                    },
                    url: "/domain/business_entity_mgt/update_business_entity/start",
                };

            if (isNameModified.value) {
                updateContent.body.name = businessEntity.value.name;
            }
            if (description.value?.isModified) {
                updateContent.body.description = description.value.blob;
            }

            if (definition.value?.isModified) {
                updateContent.body.definition = definition.value.blob;
            }

            if (isRelationshipsModified.value) {
                updateContent.body.relationships = JSON.stringify(
                    businessEntity.value.relationships
                );
            }
            if (isMetadataModified.value) {
                const entity = businessEntity.value;
                if (entity.kind !== undefined) {
                    updateContent.body.kind = entity.kind;
                }
                if (entity.confidentiality_level !== undefined) {
                    updateContent.body.confidentiality_level =
                        entity.confidentiality_level;
                }
            }

            if (
                updateContent.body.business_entity_id &&
                Object.keys(updateContent.body).length === 1
            ) {
                // Only businessEntity_id is set, no need to update.
                return;
            }

            // The entities canvas (DomainPage.vue, and the maximized canvas
            // page) shows a "Saving..." chip while a field it DRAWS is
            // outstanding — the name and the relationships — and draws the new
            // values in place of server truth until the refetch catches up.
            // Both can be edited from here, the full page, not just
            // DomainObjectDrawer.vue's quick-edit panel, so the canvas has to
            // be handed the values from here too: without them it keeps the
            // old label for the whole save and then redraws late.
            //
            // The relationships go over as the structured list rather than the
            // JSON the payload carries, because the overlay reasons about
            // individual edges to mirror them onto the object at the other end.
            if (
                updateContent.body.name !== undefined ||
                updateContent.body.relationships !== undefined
            ) {
                markCanvasSavePending(businessEntity.value.identity, {
                    name: updateContent.body.name ?? undefined,
                    relationships:
                        updateContent.body.relationships === undefined
                            ? undefined
                            : businessEntity.value.relationships.map((r) => ({
                                  ...r,
                              })),
                });
            }

            syncStore.queueSync({
                entityType: EntityType.BUSINESS_ENTITY,
                args: [updateContent],
            });

            if (description.value?.isModified) {
                description.value.persist();
            }
            if (definition.value?.isModified) {
                definition.value.persist();
            }
            if (isNameModified.value) {
                persistName();
            }
            isRelationshipsModified.value = false;
            isMetadataModified.value = false;
        };
        const reset = async () => {
            await description.value?.resetContent();
            await definition.value?.resetContent();
            resetName();
        };

        const { isEditing } = useEditManager();
        // Leaving edit mode force-closes any cell editor without firing its
        // complete/cancel events — clear the holds so refetches unblock.
        watch(isEditing, (editing) => {
            if (!editing) attributeEditHolds.value = 0;
        });
        const throttledSave = throttle(queueSave, FILE_SYNC_THROTTLE_MS);
        registerFlush(() => throttledSave.flush());
        watch([isEditing, isModified], ([_, modified]) => {
            if (modified) {
                throttledSave();
            }
        });

        const addRelationship = (relationship: BusinessEntityRelationship) => {
            businessEntity.value?.relationships.push(relationship);
            isRelationshipsModified.value = true;
        };

        const removeRelationship = (
            other_id: string,
            direction: RelationshipDirection
        ) => {
            if (!businessEntity.value) {
                return;
            }
            businessEntity.value.relationships =
                businessEntity.value.relationships.filter(
                    (r) => r.other_id !== other_id || r.direction !== direction
                );
            isRelationshipsModified.value = true;
        };

        refreshOnCrossTabEntitySync({
            ...externalRefresh,
            enabled: () => !!stek.value,
        });

        // Cross-domain linked concepts. Unlike relationships (edited inline and
        // flushed via updatebusinessEntity), linked concepts are atomically
        // replaced through a dedicated endpoint and the entity is re-fetched.
        const fetchLinkableEntities = async (): Promise<
            types.LinkableBusinessEntity[]
        > => {
            if (!businessEntity.value?.identity) return [];
            const { response, data } =
                await api.startDomainBusinessEntityMgtGetLinkableBusinessEntitiesStartPost(
                    {
                        body: {
                            business_entity_id: businessEntity.value.identity,
                        },
                    }
                );
            if (response.ok && data) return data;
            return [];
        };

        const updateLinkedConcepts = async (otherIds: string[]) => {
            if (!businessEntity.value?.identity) return;
            await api.startDomainBusinessEntityMgtUpdateLinkedConceptsStartPost(
                {
                    body: {
                        business_entity_id: businessEntity.value.identity,
                        other_ids: otherIds,
                    },
                }
            );
            if (stek.value) await updatebusinessEntity(true);
        };

        // Remove the link to `otherId` regardless of which side owns it.
        const removeLinkedConcept = async (otherId: string) => {
            if (!businessEntity.value?.identity) return;
            await api.startDomainBusinessEntityMgtRemoveLinkedConceptStartPost({
                body: {
                    business_entity_id: businessEntity.value.identity,
                    other_id: otherId,
                },
            });
            if (stek.value) await updatebusinessEntity(true);
        };

        return {
            businessEntity,
            description,
            definition,
            isFetching: computed(() =>
                Boolean(
                    isFetchingBusinessEntity.value ||
                    isFetchingDefinition.value ||
                    isFetchingDescription.value
                )
            ),
            isModified,
            reset,
            addRelationship,
            removeRelationship,
            fetchLinkableEntities,
            updateLinkedConcepts,
            removeLinkedConcept,
            markMetadataModified: () => {
                isMetadataModified.value = true;
            },
            beginAttributeEdit,
            endAttributeEdit,
        };
    }
);

export const useBusinessEntityManager = () =>
    storeToRefs(useBusinessEntityStore());
